import { ThemeEvents, CartUpdateEvent } from '@theme/events';
import { fetchConfig } from '@theme/utilities';

/**
 * Automatically adds a configured gift product to the cart once the subtotal
 * (excluding the gift line itself) reaches settings.gift_threshold, and removes
 * it again if the subtotal drops back below the threshold.
 *
 * The progress bar itself lives entirely in snippets/glo-free-gift-bar.liquid and
 * is refreshed for free whenever this file morphs a cart section, so this module
 * only ever has to reason about the cart line, never about rendering.
 */
if (!window.__gloFreeGift) {
  window.__gloFreeGift = true;

  const config = window.GLO_GIFT;

  if (config && config.variantId) {
    const PROPERTY_KEY = '_free_gift';
    const DISMISS_KEY = 'glo-gift-dismissed';

    let isRunning = false;
    let rerunQueued = false;
    // Tracks whether the gift line was present as of the last successful sync,
    // so a later disappearance while still above threshold can be recognized
    // as a manual removal rather than something this script itself did.
    let lastKnownGiftPresent = null;

    const isDismissed = () => {
      try {
        return sessionStorage.getItem(DISMISS_KEY) === '1';
      } catch (error) {
        return false;
      }
    };

    const setDismissed = () => {
      try {
        sessionStorage.setItem(DISMISS_KEY, '1');
      } catch (error) {
        // Storage unavailable (private mode, ITP, etc). Degrade silently: worst
        // case the gift may be re-added after a manual removal in this session.
      }
    };

    const getCartItemsSectionIds = () => {
      const ids = new Set();
      document.querySelectorAll('cart-items-component').forEach((element) => {
        if (element instanceof HTMLElement && element.dataset.sectionId) {
          ids.add(element.dataset.sectionId);
        }
      });
      return Array.from(ids);
    };

    // Matches by variant, not just the _free_gift property: a customer can add the
    // same (zero-priced) variant through the regular product form, which creates a
    // separate cart line without that property. Both must be treated as "the gift"
    // so the max-1-per-order and below-threshold-removal rules actually hold.
    const findGiftLines = (cart) => {
      const lines = [];
      for (let index = 0; index < cart.items.length; index++) {
        const item = cart.items[index];
        if (item.variant_id === config.variantId) {
          lines.push({ item, line: index + 1 });
        }
      }
      return lines;
    };

    const subtotalExcludingGift = (cart) =>
      cart.items.reduce((sum, item) => {
        if (item.variant_id === config.variantId) return sum;
        return sum + item.final_line_price;
      }, 0);

    const getCart = async () => {
      const response = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
      if (!response.ok) throw new Error('Failed to fetch cart');
      return response.json();
    };

    const postCart = async (url, body) => {
      const response = await fetch(url, fetchConfig('json', { body: JSON.stringify(body) }));
      const parsed = await response.json();
      if (!response.ok || parsed.status) {
        throw new Error(parsed.message || 'Cart request failed');
      }
      return parsed;
    };

    const addGift = (sectionIds) =>
      postCart(Theme.routes.cart_add_url, {
        items: [{ id: config.variantId, quantity: 1, properties: { [PROPERTY_KEY]: 'true' } }],
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      });

    const changeGiftLine = (line, quantity, sectionIds) =>
      postCart(Theme.routes.cart_change_url, {
        line,
        quantity,
        sections: sectionIds.join(','),
        sections_url: window.location.pathname,
      });

    const dispatchCartUpdate = (cart, sections) => {
      document.dispatchEvent(
        new CartUpdateEvent(cart, 'glo-free-gift', {
          source: 'glo-free-gift',
          itemCount: cart.item_count,
          sections,
        })
      );
    };

    const sync = async () => {
      if (isRunning) {
        rerunQueued = true;
        return;
      }
      isRunning = true;

      try {
        const cart = await getCart();
        const meetsThreshold = subtotalExcludingGift(cart) >= config.threshold;
        const giftLines = findGiftLines(cart);
        const totalGiftQty = giftLines.reduce((sum, giftLine) => sum + giftLine.item.quantity, 0);
        const sectionIds = getCartItemsSectionIds();

        if (giftLines.length > 1) {
          // More than one line for this variant (e.g. one added automatically, one
          // added by hand): keep whichever line already carries the _free_gift marker
          // (so the order stays annotated for the merchant) and drop the rest, one per
          // cycle, letting the resulting cart:update re-trigger sync() for any remainder.
          const keeper =
            giftLines.find((giftLine) => giftLine.item.properties?.[PROPERTY_KEY] === 'true') ?? giftLines[0];
          const extra = giftLines.find((giftLine) => giftLine.line !== keeper.line);
          const result = await changeGiftLine(extra.line, 0, sectionIds);
          lastKnownGiftPresent = true;
          dispatchCartUpdate(result, result.sections);
        } else if (giftLines.length === 1) {
          if (totalGiftQty > 1) {
            const result = await changeGiftLine(giftLines[0].line, 1, sectionIds);
            lastKnownGiftPresent = true;
            dispatchCartUpdate(result, result.sections);
          } else if (!meetsThreshold) {
            const result = await changeGiftLine(giftLines[0].line, 0, sectionIds);
            lastKnownGiftPresent = false;
            dispatchCartUpdate(result, result.sections);
          } else {
            lastKnownGiftPresent = true;
          }
        } else if (meetsThreshold) {
          if (isDismissed()) {
            lastKnownGiftPresent = false;
          } else if (lastKnownGiftPresent === true) {
            // Gift was here last sync and is gone now, even though we're still above
            // threshold: this script never removes it in that state, so the customer
            // must have removed it by hand. Respect that for the rest of the session.
            setDismissed();
            lastKnownGiftPresent = false;
          } else {
            try {
              const result = await addGift(sectionIds);
              lastKnownGiftPresent = true;
              dispatchCartUpdate(result, result.sections);
            } catch (error) {
              // Out of stock, validation error, etc. The cart must keep working regardless.
              lastKnownGiftPresent = false;
            }
          }
        } else {
          lastKnownGiftPresent = false;
        }
      } catch (error) {
        // Network or parsing failure: never block the cart, just skip this cycle.
      } finally {
        isRunning = false;
        if (rerunQueued) {
          rerunQueued = false;
          sync();
        }
      }
    };

    document.addEventListener('DOMContentLoaded', sync);
    document.addEventListener(ThemeEvents.cartUpdate, sync);
  }
}
